import * as React from 'react';
import {useState} from 'react';
import {useHistory} from 'react-router-dom';
import {ICouponModel, reportModelEmpty} from '../../models/report/IReportModel';
import {useTranslation} from 'react-i18next';
import DateUtil from '../../utils/DateUtils';
import {Views} from '../common/router/AppRouter';
import {fetchDetailReportsCSV, fetchCouponsPerReport} from '../../utils/RestClient';
import {emptyPageable} from '../../models/common/IPageable';
import Loading from '../common/Loading';
import Error from '../common/Error';
import PagerWrapper from '../common/Paging';
import Utils from '../../utils/Utils';
import {useKeycloak} from '@react-keycloak/web';
import DateUtils from '../../utils/DateUtils';

const ViewReport = () => {
  const history = useHistory<any>();
  const {t} = useTranslation();
  const {keycloak} = useKeycloak();
  if (!history.location.state?.report) {
    history.push(Views.reporting);
  }
  const [search, setSearch] = useState('');
  const [report] = useState(history.location.state?.report || reportModelEmpty);
  const [page, setPage] = useState(emptyPageable);
  const {data, error, isLoading} = fetchCouponsPerReport(report.id, page, search);
  if (isLoading && !data.content) {
    return (<Loading />);
  }
  if (error) {
    return (<Error message={t('error.fetch') + ' (' + error.message + ') '} />);
  }
  const headerClassName = 'col fw-bold';
  const valueClassName = 'col text-white p-3 align-content-center';

  function changePage(pageNbr: number) {
    setPage({...page, pageNumber: pageNbr});
  }

  function downloadCSV() {
    fetchDetailReportsCSV(keycloak, report.id).then((res: any) => {
      // For download we need to do this
      const element = document.createElement('a');
      const file = new Blob(['\uFEFF' + res.data], {type: 'text/csv'});
      element.href = URL.createObjectURL(file);
      element.download = 'detail_report_' + DateUtils.getShortStringFromDate(new Date()) + '.csv';
      document.body.appendChild(element);
      element.click();
    });
  }

  function onSearch(event: any) {
    const searchTerm = event.target.value;
    if (searchTerm && searchTerm.length > 0) {
      setSearch(searchTerm);
      changePage(0);
    } else {
      setSearch('');
      changePage(0);
    }
  }

  const pageCount = Utils.calculatePageCount(data.totalElements, page.pageSize);

  const usedCouponCount: number = report.usedCouponCount || 0;
  const unusedCouponCount: number = report.unusedCouponCount || 0;

  return (
    <>
      <div className="container-sm">
        <div className="row mb-5">
          <h1 id="top-header" className="text-center">{t('Reporting.header')}</h1>
        </div>
        <div className="row mb-5">
          <h2 className="text-center">{report.name}</h2>
        </div>
        <div id="filter-buttons" className="row w-100">
          <div className="col">
            <label htmlFor="reportSearch" className="col text-end col-form-label">{t('SearchPhrase')}</label>
            <div className="col">
              <input type="text" id="campaignSearch" name="campaignSearch" className="form-control"
                onChange={onSearch} placeholder={t('Campaigns.filter.search')} />
            </div>
          </div>
          <div className="col py-5 text-end">
            <button className="btn btn-danger" style={{opacity: '70%'}} onClick={downloadCSV}>Lataa CSV</button>
          </div>
        </div>
        <div className="row mb-3">
          <div className={headerClassName}>{t('UserTable.col.ID')}</div>
          <div className={headerClassName}>{t('UserTable.col.group')}</div>
          <div className={headerClassName}>{t('CreateCampaign.name')}</div>
          <div className={headerClassName}>{t('CampaignTable.col.starttime')}</div>
          <div className={headerClassName}>{t('CampaignTable.col.endtime')}</div>
          <div className={headerClassName}>{t('CampaignTable.col.codeamount')}</div>
          <div className={headerClassName}>{t('CampaignTable.col.valuepercode')}</div>
          <div className={headerClassName}>{t('CampaignTable.col.totalvalue')}</div>
          <div className={headerClassName}>{t('Reporting.col.used')}</div>
          <div className={headerClassName}>{t('Reporting.col.unused')}</div>
        </div>
        <div className="row align-items-center bg-primary">
          <div className={valueClassName}>{report.id}</div>
          <div className={valueClassName}><span className='limit3lines'>{report.groupNames.toString()}</span></div>
          <div className={valueClassName}>{report.name}</div>
          <div className={valueClassName}>{DateUtil.getShortStringFromDate(report.startDate)}</div>
          <div className={valueClassName}>{DateUtil.getShortStringFromDate(report.endDate)}</div>
          <div className={valueClassName}>{report.couponCount}</div>
          <div className={valueClassName}>{Utils.roundNumber(report.pricePerUser)}</div>
          <div className={valueClassName}>{Utils.roundNumber(report.couponTotalPrice)}</div>
          <div className="col reporting-used-amount-col p-3 text-center">
            <span className="border-bottom border-dark d-block">{usedCouponCount}</span>
            {Utils.roundNumber(usedCouponCount * (report.pricePerUser || 0))} €
          </div>
          <div className="col reporting-unused-amount-col p-3 text-center">
            <span
              className="border-bottom border-dark d-block">{unusedCouponCount}</span>
            {Utils.roundNumber(unusedCouponCount * (report.pricePerUser || 0))} €
          </div>
        </div>
        <div className="row mt-5">
          <table className="table table-hover">
            <thead>
              <tr>
                <th scope="col">{t('Reporting.col.receiver')}</th>
                <th scope="col">{t('Reporting.col.sender')}</th>
                <th scope="col">{t('Reporting.col.code')}</th>
                <th scope="col">{t('Reporting.col.status')}</th>
                <th scope="col">{t('Reporting.col.delivered.time')}</th>
                <th scope="col">{t('Reporting.col.activated.time')}</th>
                <th scope="col">{t('Reporting.col.expired.time')}</th>
              </tr>
            </thead>
            <tbody>
              {
                data.content?.map((item: ICouponModel) => (
                  <tr key={item.id}>
                    <td>{item.phoneNumber}</td>
                    <td>{item.sender || ''}</td>
                    <td>{item.code}</td>
                    <td>{t('couponStatus.' + item.status)}</td>
                    <td>{DateUtil.getStringFromDate(item.deliveredTimestamp)}</td>
                    <td>{DateUtil.getStringFromDate(item.activatedTimestamp)}</td>
                    <td>{DateUtil.getStringFromDate(item.expiredTimestamp)}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        {pageCount > 1 &&
          <PagerWrapper
            totalPages={pageCount}
            currentPage={page.pageNumber}
            onPageChange={(page) => changePage(page)}
          />}
        <div className="row mt-5 justify-content-end">
          <div className="col-md-1 offset-md-3">
            <button type="button" className="btn btn-primary" onClick={() => {
              history.push(Views.reporting);
            }}>{t('Exit')}</button>
          </div>
        </div>
      </div>
    </>);
};

export default ViewReport;
